import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient } from '../../utils/apiClient';

export interface AvailableSku {
  style_code: string;
  style_name: string;
  is_jeans: boolean;
  available_sizes: string[];
  available_colors: string[];
  variant_count: number;
}

export interface LocationSuggestion {
  location: string;
  type: 'bin' | 'rack';
  current_capacity: number;
  available_space: number;
  reason: string;
  priority: number;
}

export interface LocationSuggestionResponse {
  style_info: {
    style_code: string;
    style_name: string;
    is_jeans: boolean;
  };
  suggested_locations: LocationSuggestion[];
  existing_locations: Array<{
    location_name: string;
    location_type: 'bin' | 'rack';
    capacity: number;
    current_stock: number;
  }>;
}

export interface StorageLocation {
  id: number;
  location: string;
  type: 'bin' | 'rack';
  capacity: number;
  current_stock: number;
  available_space: number;
  utilization_percent: number;
  rack_type?: string;
  gender?: string;
}

export interface RecentApproval {
  id: number;
  date: string;
  gatepass_number?: string;
  style_code: string;
  color: string;
  size: string;
  quantity: number;
  sku: string;
  location: string;
  location_type: 'bin' | 'rack';
  approved_by: string;
  processed_at: string;
}

export interface ManualApprovalRequest {
  style_code: string;
  color: string;
  size?: string;
  quantity: number;
  location_type: 'bin' | 'rack';
  location_id: number;
  gatepass_number?: string;
  notes?: string;
  date?: string;
}

export interface ManualApprovalResponse {
  receipt_id: number;
  style_code: string;
  sku: string;
  quantity: number;
  location: string;
  location_type: 'bin' | 'rack';
  inventory_created: boolean;
  movement_logged: boolean;
}

interface StockApprovalState {
  // Available SKUs
  availableSkus: AvailableSku[];
  skuSearchLoading: boolean;
  skuSearchError: string | null;

  // Location suggestions
  locationSuggestions: LocationSuggestionResponse | null;
  suggestionsLoading: boolean;
  suggestionsError: string | null;

  // Available locations
  availableLocations: {
    bins: StorageLocation[];
    racks: StorageLocation[];
  };
  locationsLoading: boolean;
  locationsError: string | null;

  // Manual approval
  approvalLoading: boolean;
  approvalError: string | null;
  approvalSuccess: string | null;
  lastApproval: ManualApprovalResponse | null;

  // Recent approvals
  recentApprovals: RecentApproval[];
  recentLoading: boolean;
  recentError: string | null;
}

const initialState: StockApprovalState = {
  availableSkus: [],
  skuSearchLoading: false,
  skuSearchError: null,

  locationSuggestions: null,
  suggestionsLoading: false,
  suggestionsError: null,

  availableLocations: {
    bins: [],
    racks: []
  },
  locationsLoading: false,
  locationsError: null,

  approvalLoading: false,
  approvalError: null,
  approvalSuccess: null,
  lastApproval: null,

  recentApprovals: [],
  recentLoading: false,
  recentError: null,
};

// Async thunks
export const searchAvailableSkus = createAsyncThunk(
  'stockApproval/searchAvailableSkus',
  async ({ search, limit = 20 }: { search?: string; limit?: number }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      apiClient.setAuthToken(token);
      const result = await apiClient.getAvailableSkus(search, limit);
      
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const getLocationSuggestions = createAsyncThunk(
  'stockApproval/getLocationSuggestions',
  async (data: {
    style_code: string;
    quantity: number;
    color?: string;
    size?: string;
  }, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      apiClient.setAuthToken(token);
      const result = await apiClient.suggestStockLocation(data);
      
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAvailableLocations = createAsyncThunk(
  'stockApproval/fetchAvailableLocations',
  async ({ type, search }: { type?: 'bin' | 'rack'; search?: string } = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      apiClient.setAuthToken(token);
      const result = await apiClient.getAvailableLocations(type, search);
      
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const submitManualApproval = createAsyncThunk(
  'stockApproval/submitManualApproval',
  async (data: ManualApprovalRequest, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      apiClient.setAuthToken(token);
      const result = await apiClient.submitManualApproval(data);
      
      return result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchRecentApprovals = createAsyncThunk(
  'stockApproval/fetchRecentApprovals',
  async ({ limit = 50, days = 7 }: { limit?: number; days?: number } = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState() as any;
      const token = state.auth.token;
      
      apiClient.setAuthToken(token);
      const result = await apiClient.getRecentApprovals(limit, days);
      
      return result.data;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Slice
const stockApprovalSlice = createSlice({
  name: 'stockApproval',
  initialState,
  reducers: {
    clearAvailableSkus: (state) => {
      state.availableSkus = [];
    },
    clearLocationSuggestions: (state) => {
      state.locationSuggestions = null;
      state.suggestionsError = null;
    },
    clearApprovalMessages: (state) => {
      state.approvalError = null;
      state.approvalSuccess = null;
    },
    clearLastApproval: (state) => {
      state.lastApproval = null;
    },    resetState: (_state) => {
      return initialState;
    }
  },
  extraReducers: (builder) => {
    builder
      // Search available SKUs
      .addCase(searchAvailableSkus.pending, (state) => {
        state.skuSearchLoading = true;
        state.skuSearchError = null;
      })
      .addCase(searchAvailableSkus.fulfilled, (state, action) => {
        state.skuSearchLoading = false;
        state.availableSkus = action.payload;
      })
      .addCase(searchAvailableSkus.rejected, (state, action) => {
        state.skuSearchLoading = false;
        state.skuSearchError = action.payload as string;
      })

      // Get location suggestions
      .addCase(getLocationSuggestions.pending, (state) => {
        state.suggestionsLoading = true;
        state.suggestionsError = null;
      })
      .addCase(getLocationSuggestions.fulfilled, (state, action) => {
        state.suggestionsLoading = false;
        state.locationSuggestions = action.payload;
      })
      .addCase(getLocationSuggestions.rejected, (state, action) => {
        state.suggestionsLoading = false;
        state.suggestionsError = action.payload as string;
      })

      // Fetch available locations
      .addCase(fetchAvailableLocations.pending, (state) => {
        state.locationsLoading = true;
        state.locationsError = null;
      })
      .addCase(fetchAvailableLocations.fulfilled, (state, action) => {
        state.locationsLoading = false;
        state.availableLocations = action.payload;
      })
      .addCase(fetchAvailableLocations.rejected, (state, action) => {
        state.locationsLoading = false;
        state.locationsError = action.payload as string;
      })

      // Submit manual approval
      .addCase(submitManualApproval.pending, (state) => {
        state.approvalLoading = true;
        state.approvalError = null;
        state.approvalSuccess = null;
      })
      .addCase(submitManualApproval.fulfilled, (state, action) => {
        state.approvalLoading = false;
        state.approvalSuccess = action.payload.message;
        state.lastApproval = action.payload.data;
      })
      .addCase(submitManualApproval.rejected, (state, action) => {
        state.approvalLoading = false;
        state.approvalError = action.payload as string;
      })

      // Fetch recent approvals
      .addCase(fetchRecentApprovals.pending, (state) => {
        state.recentLoading = true;
        state.recentError = null;
      })
      .addCase(fetchRecentApprovals.fulfilled, (state, action) => {
        state.recentLoading = false;
        state.recentApprovals = action.payload;
      })
      .addCase(fetchRecentApprovals.rejected, (state, action) => {
        state.recentLoading = false;
        state.recentError = action.payload as string;
      });
  },
});

export const {
  clearAvailableSkus,
  clearLocationSuggestions,
  clearApprovalMessages,
  clearLastApproval,
  resetState
} = stockApprovalSlice.actions;

export default stockApprovalSlice.reducer;
